/**
 * @genesis_domain Auth Engine
 * @genesis_version V9.1
 * @genesis_intent Recovery OTP form presentational component — 8-digit code entry with glassmorphic styling
 * @genesis_boundaries No state management, no business logic, no API calls
 * @genesis_ai_trail V9.0: Created during tactical DDD refactor to provide OTP entry alternative to link-based password reset.
 */

import { Loader2, Mail } from 'lucide-react';
import React from 'react';

interface RecoveryOtpFormProps {
    otpCode: string;
    onOtpCodeChange: (value: string) => void;
    isVerifyingOtp: boolean;
    otpError: string | null;
    onSubmit: () => void;
    onBack: () => void;
}

export function RecoveryOtpForm({
    otpCode,
    onOtpCodeChange,
    isVerifyingOtp,
    otpError,
    onSubmit,
    onBack,
}: RecoveryOtpFormProps) {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 8);
        onOtpCodeChange(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && otpCode.length === 8 && !isVerifyingOtp) {
            onSubmit();
        }
    };

    return (
        <div className="text-center py-8 space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-success-subtle)] mb-2">
                <Mail size={32} className="text-[var(--color-success-base)]" />
            </div>

            <div className="space-y-2">
                <h3 className="text-xl font-semibold text-[var(--color-content-primary)]">Check Your Email</h3>
                <p className="text-[var(--color-content-secondary)] max-w-sm mx-auto text-sm">
                    We've sent an 8-digit code to your email. Enter it below to reset your password.
                </p>
            </div>

            {otpError && (
                <div className="mx-auto max-w-xs p-3 bg-[var(--color-danger-subtle)] border border-[var(--color-danger-base)]/20 rounded-xl">
                    <p className="text-[var(--color-danger-base)] text-sm font-medium">{otpError}</p>
                </div>
            )}

            <div className="flex justify-center">
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    value={otpCode}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. 12345678"
                    disabled={isVerifyingOtp}
                    className="w-48 h-14 text-center text-2xl font-bold tracking-[0.5em] theme-input border-2 border-[var(--color-input-border)] rounded-2xl outline-none focus:border-[var(--color-primary-base)] focus:ring-1 focus:ring-[var(--color-primary-base)] transition-all text-[var(--color-input-text)] placeholder:text-[var(--color-content-tertiary)] placeholder:tracking-normal disabled:opacity-50"
                    autoFocus
                />
            </div>

            <div className="space-y-3">
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={otpCode.length !== 8 || isVerifyingOtp}
                    className="w-full max-w-xs mx-auto h-14 bg-[var(--color-primary-base)] hover:bg-[var(--color-primary-hover)] disabled:bg-[var(--color-primary-base)]/50 text-[var(--color-content-inverse)] rounded-2xl text-lg font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-google hover:shadow-google-lg"
                >
                    {isVerifyingOtp ? (
                        <Loader2 className="animate-spin" size={24} />
                    ) : (
                        <span>Confirm Code</span>
                    )}
                </button>

                <button
                    type="button"
                    onClick={onBack}
                    disabled={isVerifyingOtp}
                    className="text-[var(--color-primary-base)] font-medium hover:underline text-sm"
                >
                    Back to Sign In
                </button>
            </div>
        </div>
    );
}
